import api from './api'
import { ApiResponse } from '../types/api'
import {
  AdminCoupon,
  AdminCouponDetail,
  AdminCouponPayload,
  AdminCouponUsage
} from '../types/adminCoupons'

type AdminCouponApi = {
  id: string
  code: string
  name?: string
  description?: string
  type: string
  value: number
  minOrderValue?: number
  maxDiscount?: number
  usageLimit?: number
  usedCount: number
  validFrom: string
  validUntil: string
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

type AdminCouponUsageApi = {
  id: string
  customerName: string
  orderId?: string
  usedAt: string
  discountValue?: number
}

const extractData = <T>(payload: ApiResponse<T> | { success?: boolean; data: T } | T): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    const data = (payload as { data: T }).data
    if (data !== undefined) {
      return data
    }
  }
  return payload as T
}

const mapCoupon = (coupon: AdminCouponApi): AdminCoupon => ({
  id: coupon.id,
  code: coupon.code,
  name: coupon.name,
  description: coupon.description,
  type: (coupon.type?.toLowerCase() as AdminCoupon['type']) ?? 'percentage',
  value: coupon.value,
  minOrderValue: coupon.minOrderValue,
  maxDiscount: coupon.maxDiscount,
  maxUses: coupon.usageLimit,
  usedCount: coupon.usedCount,
  validFrom: coupon.validFrom,
  validUntil: coupon.validUntil,
  isActive: coupon.isActive,
  createdAt: coupon.createdAt,
  updatedAt: coupon.updatedAt
})

const mapUsage = (usage: AdminCouponUsageApi): AdminCouponUsage => ({
  id: usage.id,
  customerName: usage.customerName,
  orderId: usage.orderId,
  usedAt: usage.usedAt,
  discountValue: usage.discountValue
})

const normalizePayload = (payload: AdminCouponPayload) => ({
  code: payload.code.trim().toUpperCase(),
  type: payload.type,
  value: payload.value,
  minOrderValue:
    payload.minOrderValue === undefined ? undefined : payload.minOrderValue,
  maxDiscount:
    payload.maxDiscount === undefined ? undefined : payload.maxDiscount,
  usageLimit:
    payload.maxUses === undefined ? undefined : payload.maxUses,
  validFrom: payload.validFrom,
  validUntil: payload.validUntil,
  isActive: payload.isActive,
  description: payload.description?.trim(),
  name: payload.name?.trim()
})

const adminCouponService = {
  async list(params?: {
    search?: string
    isActive?: boolean
    page?: number
    limit?: number
  }): Promise<AdminCoupon[]> {
    const response = await api.get<
      ApiResponse<{ coupons: AdminCouponApi[]; pagination?: any }> | 
      { coupons: AdminCouponApi[]; pagination?: any } |
      AdminCouponApi[]
    >('/admin/coupons', { params })

    const data = extractData(response.data)
    
    // Se for array direto (formato antigo), retornar como está
    if (Array.isArray(data)) {
      return (data as AdminCouponApi[]).map(mapCoupon)
    }
    
    // Se tiver estrutura com coupons
    const coupons = (data as { coupons?: AdminCouponApi[] }).coupons
    if (Array.isArray(coupons)) {
      return coupons.map(mapCoupon)
    }
    
    // Fallback: tentar como array
    return (data as unknown as AdminCouponApi[]).map(mapCoupon)
  },

  async getById(couponId: string): Promise<AdminCouponDetail> {
    const response = await api.get<
      ApiResponse<{
        coupon: AdminCouponApi
        usageLog: AdminCouponUsageApi[]
      }> | {
        coupon: AdminCouponApi
        usageLog: AdminCouponUsageApi[]
      }
    >(`/admin/coupons/${couponId}`)

    const data = extractData(response.data)
    return {
      coupon: mapCoupon(data.coupon),
      usageLog: Array.isArray(data.usageLog)
        ? data.usageLog.map(mapUsage)
        : []
    }
  },

  async create(payload: AdminCouponPayload): Promise<AdminCoupon> {
    const response = await api.post<
      ApiResponse<AdminCouponApi> | AdminCouponApi
    >('/admin/coupons', normalizePayload(payload))

    const data = extractData(response.data)
    return mapCoupon(data)
  },

  async update(
    couponId: string,
    payload: AdminCouponPayload
  ): Promise<AdminCoupon> {
    const response = await api.put<
      ApiResponse<AdminCouponApi> | AdminCouponApi
    >(`/admin/coupons/${couponId}`, normalizePayload(payload))

    const data = extractData(response.data)
    return mapCoupon(data)
  },

  async updateStatus(couponId: string, isActive: boolean): Promise<AdminCoupon> {
    const response = await api.patch<
      ApiResponse<AdminCouponApi> | AdminCouponApi
    >(`/admin/coupons/${couponId}/status`, { isActive })

    const data = extractData(response.data)
    return mapCoupon(data)
  },

  async remove(couponId: string): Promise<void> {
    await api.delete(`/admin/coupons/${couponId}`)
  }
}

export default adminCouponService


